import { useEffect, useState } from 'react';
import { getAllFaculty, createFaculty, updateFaculty } from '../../../api/training';
import { toast } from 'react-toastify';
import FacultyHeader from './components/FacultyHeader';
import FacultyTable from './components/FacultyTable';
import FacultyModal from './components/FacultyModal';

const Faculty = () => {
  const initialFaculty = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    totalEnrollment: '',
    status: 'Active',
    misc: '',
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFacultyId, setEditingFacultyId] = useState(null);
  const [currentFaculty, setCurrentFaculty] = useState(initialFaculty);
  const [facultyList, setFacultyList] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [accessDenied, setAccessDenied] = useState(false);

  const openModalForNewFaculty = () => {
    setIsEditMode(false);
    setEditingFacultyId(null);
    setCurrentFaculty(initialFaculty);
    setIsModalOpen(true);
  };

  const openModalForUpdate = (facultyId) => {
    const facultyData = facultyList.find(
      (f) => f.faculty_id === facultyId || f.id === facultyId
    );
    if (!facultyData) {
      toast.error('Faculty not found');
      return;
    }

    setIsEditMode(true);
    setEditingFacultyId(facultyData.faculty_id || facultyData.id);
    setCurrentFaculty({
      firstName: facultyData.first_name || '',
      lastName: facultyData.last_name || '',
      email: facultyData.email || '',
      phone: facultyData.phone || '',
      totalEnrollment: facultyData.total_enrollment || '',
      status: facultyData.status || 'Active',
      misc: typeof facultyData.misc === 'object'
        ? JSON.stringify(facultyData.misc)
        : facultyData.misc || '',
      // Display-only fields (not editable)
      created: facultyData.created || '',
      created_by: facultyData.created_by || '',
      updated: facultyData.updated || '',
      updated_by: facultyData.updated_by || '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingFacultyId(null);
    setCurrentFaculty(initialFaculty);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      // Helper to parse integer safely
      const toInteger = (value) => {
        if (!value && value !== 0) return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
      };

      // Helper to strip phone formatting for API (digits only)
      const stripPhoneFormat = (phone) => {
        if (!phone) return '';
        const digits = String(phone).replace(/\D/g, '');
        return digits;
      };

      // Build faculty data matching API schema
      const facultyData = {};

      // Required fields - always send these
      facultyData.firstName = formData.firstName?.trim() || '';
      facultyData.lastName = formData.lastName?.trim() || '';
      facultyData.email = formData.email?.trim() || '';
      facultyData.phone = stripPhoneFormat(formData.phone);

      // Optional fields
      const totalEnrollment = toInteger(formData.totalEnrollment);
      if (totalEnrollment !== null) facultyData.totalEnrollment = totalEnrollment;

      if (formData.status?.trim()) facultyData.status = formData.status.trim();

      if (formData.misc?.trim()) facultyData.misc = formData.misc.trim();

      console.log('Faculty payload being sent:', facultyData);

      if (isEditMode) {
        await updateFaculty(editingFacultyId, facultyData);
        toast.success('Faculty Updated Successfully');
      } else {
        await createFaculty(facultyData);
        toast.success('Faculty Created Successfully');
      }

      closeModal();
      getData();
    } catch (error) {
      console.error('Error saving faculty:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        toast.error('Access denied: You do not have permission to manage faculty');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 400) {
        toast.error(errorMessage || 'Invalid data. Please check your input');
      } else {
        toast.error(errorMessage || 'Failed to save faculty');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getData = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const response = await getAllFaculty(1, 100);
      console.log('Faculty API response:', response);

      if (response && response.data) {
        const data = response.data;
        // Handle structure: { message, data: { totalRecords, pageNum, pageSize, records } }
        const records = data.data?.records || data.records || [];
        console.log('Faculty list extracted:', records);
        setFacultyList(Array.isArray(records) ? records : []);
      } else if (Array.isArray(response)) {
        setFacultyList(response);
      } else {
        setFacultyList([]);
      }
    } catch (error) {
      console.error('Error fetching faculty:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      // Check for 403 Forbidden - show on UI instead of toast
      if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        setAccessDenied(true);
      } else if (error.message === 'Network Error' || !error.response) {
        setAccessDenied(true);
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again later');
      } else {
        toast.error(errorMessage || 'Failed to load faculty');
      }
      setFacultyList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter faculty based on search term
  const filteredFaculty = facultyList.filter((faculty) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${faculty.first_name || ''} ${faculty.last_name || ''}`.trim().toLowerCase();
    return (
      fullName.includes(searchLower) ||
      faculty.first_name?.toLowerCase().includes(searchLower) ||
      faculty.last_name?.toLowerCase().includes(searchLower) ||
      faculty.email?.toLowerCase().includes(searchLower) ||
      faculty.phone?.toLowerCase().includes(searchLower) ||
      faculty.status?.toLowerCase().includes(searchLower)
    );
  });

  // Show access denied screen
  if (accessDenied) {
    return (
      <div className="p-5">
        <div className="border border-gray-300 rounded-lg">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Denied</h2>
            <p className="text-gray-500 text-center max-w-md">
              You are not authorized to manage faculty. Please contact your Admin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* Header with actions */}
      <div className="border border-gray-300 rounded-lg relative">
        <FacultyHeader
          onReload={getData}
          onAddFaculty={openModalForNewFaculty}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <FacultyTable
            data={filteredFaculty}
            onEditFaculty={openModalForUpdate}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Faculty Modal */}
      <FacultyModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        defaultValues={currentFaculty}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Faculty;
